import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../../context/breadcrumb.context'
import { useParamsInt } from '../../../../../../shared/hooks/location'
import { useLoad } from '../../../../../../shared/hooks/load'
import { IconShield } from '../../../../../../shared/icon/icon'
import { useRoleSysProvider } from '../../../../../../role/hook/role-sys'
import { useOrganizationName } from '../../../../../../shared/org-search/use-organization-names'
import { useNotificationContext } from '../../../../../../context/notification/context'
import {
  useApiFormOrgEntitlement,
  useApiListOrgEntitlement,
  useApiFormProviderDomain,
  useApiListProviderDomain,
  useApiListProviderDomainRollup,
  useApiListProviderOrganinization
} from '../../../../../../api-new/state/auth'
import { domainTone, overrideDetail } from '../../../../../../shared/component/status-pill'
import { normalizeDomain } from '../../../../../../shared/form-new/form-tags'
import { WorkspaceSection } from '../../../../../../shared/sso-workspace/workspace-shell'
import { DomainPill } from '../../../../../../shared/sso-workspace/pill'
import { connectionUse, ConnectionUsePill } from '../../../../../../shared/sso-workspace/connection-use'
import { ProviderLogo } from '../../../../../../shared/sso-workspace/provider-logo'
import { TXT_KEEP_HINT, TxtRecordFields } from '../../../../../../shared/sso-workspace/txt-record'
import { checkLine, COOLDOWN_CODE, DOUBLED_CODE } from '../../../../../../shared/sso-workspace/domain-check'
import { UnbindDialog } from '../../../../../../shared/sso-workspace/unbind-dialog'
import { RequireSSOSwitch } from '../../../../../../shared/sso-workspace/require-sso'
import '../../../../../../shared/sso-consent/sso-consent.scss'
import { providerOrganizationRuleLabel } from '../../../provider/page/provider-organization-rule'
import {
  ProviderDomainOverrideModal,
  ProviderDomainRemoveModal,
  ProviderDomainRowMenu,
  ProviderDomainTransferModal
} from '../../../provider/page/provider-domain-actions'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'

const serverMessage = (err, fallback) => (err && err.data && err.data.message) || fallback

const formatDate = value =>
  new Date(value).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

const CONNECTION_KIND = { shared_app: "EducationXR's shared app", own_registration: 'Own app registration' }

// Sys-admin SSO tab on the organization edit page, as one summary in the
// connection workspace's visual language (spec 2026-09-26 design pass):
//
//   - Plan: the entitlement switch, what it enables, and the last SSO
//     sign-in as a sentence.
//   - Connections: every connection linked to the organization as a row
//     (logo, name, who owns it, its rule, status, a link to its workspace);
//     empty, it offers "Connect Microsoft or Google" prefilled for this
//     organization.
//   - Domains: "Add domain" claims a domain for THIS organization (the sys
//     route takes an explicit owner), each row shows its state, the TXT
//     record to publish until DNS proves it, the connections it is bound to
//     with Unbind (audit B1: staff had no way to bind or unbind), a Bind
//     picker of the organization's connections, the Require SSO switch with
//     what it does under it, and the existing row menu (check, override,
//     transfer, notify, remove).
//
// The entitlement flag's GET may still 404 on an older backend: the read is
// wrapped so a failed read degrades to an unchecked switch.
export const OrganizationSso = () => {
  const { organizationID } = useParamsInt()

  const roleSysProvider = useRoleSysProvider()
  const notificationContext = useNotificationContext()

  const apiListOrgEntitlement = useApiListOrgEntitlement()
  const apiFormOrgEntitlement = useApiFormOrgEntitlement()
  const apiListProviderOrganinization = useApiListProviderOrganinization()
  const apiListProviderDomain = useApiListProviderDomain(`?organizationID=${organizationID}`)
  const apiFormProviderDomain = useApiFormProviderDomain()
  const apiListProviderDomainRollup = useApiListProviderDomainRollup(`?organizationID=${organizationID}`)

  const [featureSSO, setFeatureSSO] = useState(false)
  const [entitlementError, entitlementFailure, setEntitlementError] = useErrorLine()

  const [overrideDomain, setOverrideDomain] = useState(null)
  const [transferDomain, setTransferDomain] = useState(null)
  const [removeDomain, setRemoveDomain] = useState(null)

  // Each read stands alone: one failing request (the rollup once 500'd) must
  // not leave the plan switch showing "off" for an organization that has it.
  useLoad(async () => {
    const readEntitlement = async () => {
      try {
        const entitlement = await apiListOrgEntitlement.get(organizationID)
        setFeatureSSO(!!(entitlement && entitlement.featureSSO))
      } catch (err) {
        setEntitlementError("The plan couldn't be read. Reload to try again.", err)
      }
    }
    await Promise.allSettled([
      apiListProviderOrganinization.get(`${organizationID}/organization`),
      apiListProviderDomain.get(),
      apiListProviderDomainRollup.get(),
      readEntitlement()
    ])
  }, [organizationID])

  // This organization's name, by id (never the whole organization list).
  const organizationName = useOrganizationName(organizationID)

  const handleToggleFeatureSSO = async e => {
    const next = e.target.checked
    setEntitlementError(null)

    try {
      await apiFormOrgEntitlement.put(organizationID, { featureSSO: next })
      setFeatureSSO(next)
    } catch (err) {
      setEntitlementError(serverMessage(err, "The plan couldn't be updated."), err)
    }
  }

  const handleRefreshDomains = async () => await apiListProviderDomain.get()
  const handleRefreshConnections = async () => await apiListProviderOrganinization.get(`${organizationID}/organization`)

  // Check now says what DNS showed (domain-check.js's words), or auth's own
  // sentence for the doubled name and the cooldown.
  const handleCheck = async domain => {
    try {
      const result = await apiFormProviderDomain.postArg(`/${domain.id}/check`)
      const line = checkLine(domain.domain, result)
      if (line) notificationContext.toast.notify(line.text)
    } catch (err) {
      const body = (err && err.data) || {}
      if (body.code === COOLDOWN_CODE || body.code === DOUBLED_CODE) notificationContext.toast.notify(body.message)
      else throw err
    }
    await handleRefreshDomains()
  }

  const handleLiftOverride = async domain => {
    await apiFormProviderDomain.delete(`${domain.id}/override`)
    await handleRefreshDomains()
  }

  const handleToggleSSORequired = async domain => {
    await apiFormProviderDomain.put(`${domain.id}/sso-required`, { required: !domain.ssoRequired })
    await handleRefreshDomains()
  }

  const handleNotify = async domain => {
    const result = await apiFormProviderDomain.postArg(`/${domain.id}/notify-members`)
    const sent = (result && result.sent) || 0
    notificationContext.toast.notify(`notified ${sent} member${sent === 1 ? '' : 's'}`)
  }

  const handleOverrideSubmit = async ({ reason, until }) => {
    await apiFormProviderDomain.put(`${overrideDomain.id}/override`, { reason, until })
    setOverrideDomain(null)
    await handleRefreshDomains()
  }

  const handleTransferSubmit = async transferOrganizationID => {
    await apiFormProviderDomain.put(`${transferDomain.id}/owner`, { organizationID: transferOrganizationID })
    setTransferDomain(null)
    await handleRefreshDomains()
  }

  const handleRemoveConfirm = async () => {
    await apiFormProviderDomain.delete(removeDomain.id)
    setRemoveDomain(null)
    await handleRefreshDomains()
    // its bindings went with it: the connections' status and counts change
    await handleRefreshConnections()
  }

  const connections = apiListProviderOrganinization.items
  const rollup = apiListProviderDomainRollup.items || {}
  const canEdit = Boolean(roleSysProvider.accessUpdate)

  return (
    <BreadcrumbView Icon={IconShield} text="sso">
      <div className="sw-workspace sw-pane">
        <header className="mb-3">
          <h1 className="sw-title">Single sign-on{organizationName ? ` for ${organizationName}` : ''}</h1>
          <div className="sw-sub">
            {rollup.lastSSOSignInAt
              ? `The last single sign-on sign-in here was on ${formatDate(rollup.lastSSOSignInAt)}.`
              : 'Nobody here has signed in with single sign-on yet.'}
          </div>
        </header>

        <WorkspaceSection id="plan" title="Plan">
          <div className="form-check form-switch mb-1">
            <input
              className="form-check-input"
              type="checkbox"
              id="organization-sso-feature-switch"
              checked={featureSSO}
              disabled={!canEdit}
              onChange={handleToggleFeatureSSO}
            />
            <label className="form-check-label fw-semibold" htmlFor="organization-sso-feature-switch">
              Single sign-on is included in this organization's plan
            </label>
          </div>
          <p className="sw-hint mb-0">
            Lets this organization's admins connect Microsoft or Google sign-in and manage their domains. Without it, people here can't join through single sign-on.
          </p>
          {entitlementError && <p className="text-danger small mt-2 mb-0" role="alert">{entitlementError}</p>}
          {entitlementError && <ErrorReference error={entitlementFailure} />}
        </WorkspaceSection>

        <WorkspaceSection id="connections" title="Connections" lede="The identity providers people here sign in through, and who manages each one.">
          {connections.length === 0
            ? <div className="sc-empty-state">
              <p>No connections yet. Connect Microsoft or Google so people here can sign in with their work accounts.</p>
              {roleSysProvider.accessCreate &&
                <Link className="btn sw-btn sw-btn-primary" to={`/admin/sso/connect?organizationID=${organizationID}`}>Connect Microsoft or Google</Link>}
            </div>
            : <>
              <ul className="sc-rows">
                {connections.map(link => {
                  // Switched on, not public, and no domain of ANY organization
                  // bound (providerBindingCount): nobody is sent to it.
                  const use = connectionUse({ active: link.providerActive, isPublic: link.providerPublic, bound: link.providerBindingCount })
                  return (
                    <li key={link.providerID} className="sc-row" data-testid={`connection-${link.providerID}`}>
                      <ProviderLogo iconSVG={link.providerIconSVG} iconSVGDark={link.providerIconSVGDark} />
                      <div className="sc-row-main">
                        <span className="fw-semibold">{link.providerDisplayName || link.providerName}</span>
                        <span className="sw-hint">
                          {[
                            CONNECTION_KIND[link.providerConnection],
                            link.providerOwnerID === organizationID ? 'Owned by this organization' : link.providerOwnerID ? 'Owned by another organization' : 'Managed by EducationXR',
                            providerOrganizationRuleLabel(link.matchData)
                          ].filter(Boolean).join(' · ')}
                        </span>
                        {use.reason && <span className="sw-hint">{use.reason}</span>}
                      </div>
                      <ConnectionUsePill use={use} />
                      <Link className="btn btn-sm sw-btn" to={`/admin/sso/connections/${link.providerID}`}>
                        Open<span className="visually-hidden"> {link.providerDisplayName || link.providerName}</span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
              {roleSysProvider.accessCreate &&
                <p className="sw-hint mt-2 mb-0"><Link to={`/admin/sso/connect?organizationID=${organizationID}`}>Connect another provider</Link></p>}
            </>}
        </WorkspaceSection>

        <WorkspaceSection id="domains" title="Domains" lede="The email domains people here sign in with. A domain sends people to its bound connections once DNS proves it, or while an override is in place.">
          <OrganizationDomains
            organizationID={organizationID}
            domains={apiListProviderDomain.items}
            connections={connections}
            roleSysProvider={roleSysProvider}
            onChanged={handleRefreshDomains}
            onConnectionsChanged={handleRefreshConnections}
            onCheck={handleCheck}
            onOpenOverride={setOverrideDomain}
            onLiftOverride={handleLiftOverride}
            onOpenTransfer={setTransferDomain}
            onToggleSSORequired={handleToggleSSORequired}
            onNotify={handleNotify}
            onOpenRemove={setRemoveDomain}
          />
        </WorkspaceSection>
      </div>

      <ProviderDomainOverrideModal
        domain={overrideDomain}
        enabled={canEdit}
        onSubmit={handleOverrideSubmit}
        onCancel={() => setOverrideDomain(null)}
      />
      <ProviderDomainTransferModal
        domain={transferDomain}
        enabled={canEdit}
        onSubmit={handleTransferSubmit}
        onCancel={() => setTransferDomain(null)}
      />
      <ProviderDomainRemoveModal
        domain={removeDomain}
        enabled={Boolean(roleSysProvider.accessDelete)}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveDomain(null)}
      />
    </BreadcrumbView>
  )
}

// Add a domain for this organization, then its rows.
const OrganizationDomains = ({ organizationID, domains, connections, roleSysProvider, onChanged, onConnectionsChanged, ...menu }) => {
  const apiFormProviderDomain = useApiFormProviderDomain()
  const canEdit = Boolean(roleSysProvider.accessUpdate)

  const [value, setValue] = useState('')
  const [hint, setHint] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')
  const [unbinding, setUnbinding] = useState(null) // { domain, connection }
  const [unbindError, unbindFailure, setUnbindError] = useErrorLine()

  const handleAdd = async e => {
    e.preventDefault()
    setError(null)
    const domain = normalizeDomain(value)
    if (!domain) {
      setHint(value.trim() ? `"${value.trim()}" isn't a domain.` : 'Enter the email domain, for example example.org.')
      return
    }
    setHint(null)
    setBusy(true)
    try {
      await apiFormProviderDomain.postArg('', { domain, organizationID })
      setValue('')
      setStatus(`Added ${domain}. Publish its TXT record, or override it, and bind it to a connection.`)
      await onChanged()
    } catch (err) {
      setError(serverMessage(err, `${domain} couldn't be added.`), err)
    } finally {
      setBusy(false)
    }
  }

  const handleBind = async (domain, providerID) => {
    setError(null)
    try {
      await apiFormProviderDomain.postArg(`/${domain.id}/bindings`, { providerID: Number(providerID) })
      const connection = connections.find(c => c.providerID === Number(providerID))
      setStatus(`Bound ${domain.domain} to ${connection ? connection.providerDisplayName || connection.providerName : 'the connection'}.`)
      await onChanged()
      // A routable domain takes a waiting connection live on bind.
      await onConnectionsChanged()
    } catch (err) {
      setError(serverMessage(err, `${domain.domain} couldn't be bound.`), err)
    }
  }

  const handleUnbind = async deactivate => {
    const { domain, connection } = unbinding
    setBusy(true)
    setUnbindError(null)
    try {
      await apiFormProviderDomain.delete(`${domain.id}/bindings/${connection.providerID}${deactivate ? '?deactivate=true' : ''}`)
    } catch (err) {
      setUnbindError(serverMessage(err, `${domain.domain} couldn't be unbound.`), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setUnbinding(null)
    setStatus(`Unbound ${domain.domain}${deactivate ? ' and deactivated the connection' : ''}.`)
    await onChanged()
    // The connection's status (Active / Not in use) and binding count (the
    // last-domain warning) change with any unbind, not only a deactivation.
    await onConnectionsChanged()
  }

  // The unbind warning's "last domain" is the connection's last binding
  // across EVERY organization (providerBindingCount, from the server), not
  // this organization's: a shared connection must never be offered for
  // deactivation while another customer still routes through it (review I4).
  const isLastOfActive = connection =>
    Boolean(connection.providerActive && !connection.providerPublic && typeof connection.providerBindingCount === 'number' && connection.providerBindingCount <= 1)

  return (
    <div className="d-grid gap-3">
      {canEdit &&
        <form className="d-flex flex-wrap align-items-start gap-2" noValidate onSubmit={handleAdd}>
          <div className="sw-field flex-grow-1" style={{ maxWidth: 360 }}>
            <label className="sw-label" htmlFor="organization-sso-add-domain">Add a domain</label>
            <input id="organization-sso-add-domain" className="form-control" value={value} placeholder="example.org" autoComplete="off" spellCheck={false}
              aria-describedby="organization-sso-add-domain-hint" onChange={e => setValue(e.target.value)} />
            <div id="organization-sso-add-domain-hint" className={hint ? 'sw-required' : 'sw-hint'}>{hint || 'Claimed for this organization. It needs a DNS record, or an override, before anyone is sent to SSO.'}</div>
          </div>
          <button type="submit" className="btn sw-btn sw-btn-primary" style={{ marginTop: '1.6rem' }} disabled={busy}>Add domain</button>
        </form>}

      <div className="sw-subh">
        <p role="status" aria-live="polite" className="sw-status text-start">{status}</p>
        <Link className="btn btn-sm sw-btn" to="/admin/sso/domains">All domains</Link>
      </div>

      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mt-0" />}

      {domains.length === 0
        ? <p className="sw-hint m-0">No domains yet. Add the email domain people here sign in with.</p>
        : <ul className="sw-rows">
          {domains.map(domain =>
            <OrganizationDomainRow key={domain.id} domain={domain} connections={connections} roleSysProvider={roleSysProvider}
              onBind={handleBind} onAskUnbind={connection => { setUnbindError(null); setUnbinding({ domain, connection }) }} {...menu} />)}
        </ul>}

      <UnbindDialog
        open={Boolean(unbinding)}
        domain={unbinding ? unbinding.domain.domain : ''}
        connectionName={unbinding ? unbinding.connection.providerDisplayName || unbinding.connection.providerName : ''}
        lastOfActive={Boolean(unbinding && isLastOfActive(unbinding.connection))}
        busy={busy}
        error={unbindError}
        failure={unbindFailure}
        onUnbind={() => handleUnbind(false)}
        onUnbindAndDeactivate={() => handleUnbind(true)}
        onCancel={() => setUnbinding(null)}
      />
    </div>
  )
}

const OrganizationDomainRow = ({ domain, connections, roleSysProvider, onBind, onAskUnbind, onToggleSSORequired, ...menu }) => {
  const [selected, setSelected] = useState('')
  const canEdit = Boolean(roleSysProvider.accessUpdate)
  const tone = domainTone(domain)
  const boundIDs = domain.providerIDs || []
  const bound = connections.filter(c => boundIDs.includes(c.providerID))
  const bindable = connections.filter(c => !boundIDs.includes(c.providerID))
  const name = c => c.providerDisplayName || c.providerName

  return (
    <li className="sw-domain d-grid gap-2" data-testid={`domain-${domain.id}`}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="d-inline-flex flex-wrap align-items-center gap-2">
          <code className="fw-semibold">{domain.domain}</code>
          <DomainPill tone={tone} title={tone === 'overridden' ? undefined : overrideDetail(domain) || undefined} />
          {domain.state === 'at_risk' && domain.deadlineAt &&
            <span className="sw-hint">Verify again by {formatDate(domain.deadlineAt)}</span>}
          {tone === 'overridden' && <span className="sw-hint">{overrideDetail(domain)}</span>}
        </span>
        <span data-testid="domain-menu">
          <ProviderDomainRowMenu domain={domain} roleSysProvider={roleSysProvider} {...menu} />
        </span>
      </div>

      {!domain.lastOkAt &&
        <React.Fragment>
          <div className="sw-panel">
            <p className="sw-hint m-0">Publish this TXT record at the domain's DNS host to verify it:</p>
            <TxtRecordFields idPrefix={`txt-${domain.id}`} domain={domain.domain} token={domain.txtToken} fullName={domain.txtName} relativeName={domain.txtRelativeName} dnsHost={domain.dnsHost} />
          </div>
          <p className="sw-hint m-0">{TXT_KEEP_HINT}</p>
        </React.Fragment>}

      {/* Require SSO is a switch here, not a kebab item, so it can say what
          it does (owner 2026-10-06). */}
      <RequireSSOSwitch id={`organization-sso-domain-required-${domain.id}`} domain={domain.domain} checked={domain.ssoRequired}
        disabled={!canEdit} onChange={() => onToggleSSORequired(domain)} />

      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="sw-hint">Sends people to:</span>
        {bound.length === 0 && <span className="sw-hint">no connection yet</span>}
        {bound.map(c =>
          <span key={c.providerID} className="sw-tag">
            {name(c)}
            {canEdit &&
              <button type="button" className="sw-tag-x" aria-label={`Unbind ${domain.domain} from ${name(c)}`} onClick={() => onAskUnbind(c)}>×</button>}
          </span>)}
      </div>

      {canEdit && bindable.length > 0 &&
        <div className="d-flex flex-wrap align-items-center gap-2">
          <select className="form-select form-select-sm w-auto" aria-label={`Bind ${domain.domain} to a connection`} value={selected} onChange={e => setSelected(e.target.value)}>
            <option value="">Choose a connection</option>
            {bindable.map(c => <option key={c.providerID} value={c.providerID}>{name(c)}</option>)}
          </select>
          <button type="button" className="btn btn-sm sw-btn" disabled={!selected} onClick={async () => { await onBind(domain, selected); setSelected('') }}>Bind</button>
        </div>}
      {canEdit && connections.length === 0 &&
        <p className="sw-hint m-0">Connect a provider for this organization to bind this domain to it.</p>}
    </li>
  )
}
